import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client";

/**
 * Minimal data-fetching hooks.
 *
 * A query cache library would be the obvious reach here, but this console's whole
 * data model is "one screen, one endpoint, refetch after a write" — the caching,
 * invalidation and background-refresh machinery would sit unused while adding a
 * dependency to audit. If cross-screen cache sharing ever earns its keep, this is
 * the seam to replace.
 */

export interface Resource<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  /** Re-run the request; used after a mutation and by the manual refresh button. */
  reload: () => void;
}

export function useResource<T>(path: string | null, deps: unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  // Guards against a slow first response overwriting a newer one, and against
  // setting state on an unmounted component.
  const latest = useRef(0);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    const ticket = ++latest.current;
    setLoading(true);
    setError(undefined);
    api
      .get<T>(path)
      .then((result) => {
        if (ticket !== latest.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (ticket !== latest.current) return;
        setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
        setLoading(false);
      });
    return () => {
      // Invalidate this request's ticket, so its resolution is ignored.
      if (ticket === latest.current) latest.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

export interface Mutation<TArgs> {
  run: (args: TArgs) => Promise<boolean>;
  pending: boolean;
  error: ApiError | undefined;
  reset: () => void;
}

/**
 * Wraps a write so every caller gets the same pending/error handling. Resolves to
 * `true` on success and `false` on a handled failure, so a form can decide whether
 * to close without needing a try/catch of its own.
 */
export function useMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>): Mutation<TArgs> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);

  const run = useCallback(
    async (args: TArgs) => {
      setPending(true);
      setError(undefined);
      try {
        await fn(args);
        return true;
      } catch (err) {
        setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
        return false;
      } finally {
        setPending(false);
      }
    },
    [fn],
  );

  return { run, pending, error, reset: () => setError(undefined) };
}
